import { LabelCard } from 'components/cards/LabelCard.tsx';
import { Skeleton } from 'components/ui/skeleton';
import React from 'react';
import { useGetComplianceStatisticsQuery } from 'store/api/contacts.ts';
import { ComplianceStatisticsFilter } from 'types/compliance.ts';
import { cn } from 'utils/cn';

const getColor = (count: number): 'error' | 'success' => (count > 0 ? 'error' : 'success');

const GRID_CLASS = 'mb-6 new:mb-0 grid grid-cols-2 gap-4 new:gap-(--card-gap) sm:grid-cols-3 md:grid-cols-5';

const DashboardSkeleton: React.FC = () => (
    <div className={GRID_CLASS}>
        {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
        ))}
    </div>
);

interface DashboardCard {
    filterValue: ComplianceStatisticsFilter;
    labelId: string;
    countKey: 'onboardingNewClients' | 'activeNotVerified' | 'missingChecks' | 'soonExpiring' | 'activePendingAck';
    tooltipText: string;
}

const CARDS: DashboardCard[] = [
    {
        filterValue: ComplianceStatisticsFilter.ONBOARDING_NEW_CLIENTS,
        labelId: 'compliance.dashboard.onboarding-new-clients',
        countKey: 'onboardingNewClients',
        tooltipText: 'compliance.dashboard.onboarding-new-clients-tooltip',
    },
    {
        filterValue: ComplianceStatisticsFilter.ACTIVE_NOT_VERIFIED,
        labelId: 'compliance.dashboard.active-not-verified',
        countKey: 'activeNotVerified',
        tooltipText: 'compliance.dashboard.active-not-verified-tooltip',
    },
    {
        filterValue: ComplianceStatisticsFilter.MISSING_CHECKS,
        labelId: 'compliance.dashboard.missing-checks',
        countKey: 'missingChecks',
        tooltipText: 'compliance.dashboard.missing-checks-tooltip',
    },
    {
        filterValue: ComplianceStatisticsFilter.SOON_EXPIRING,
        labelId: 'compliance.dashboard.soon-expiring',
        countKey: 'soonExpiring',
        tooltipText: 'compliance.dashboard.soon-expiring-tooltip',
    },
    {
        filterValue: ComplianceStatisticsFilter.ACTIVE_PENDING_ACK,
        labelId: 'compliance.dashboard.active-pending-ack',
        countKey: 'activePendingAck',
        tooltipText: 'compliance.dashboard.active-pending-ack-tooltip',
    },
];

interface ComplianceDashboardProps {
    activeFilter: ComplianceStatisticsFilter | null;
    onFilterChange: (filter: ComplianceStatisticsFilter | null) => void;
}

const ComplianceDashboard: React.FC<ComplianceDashboardProps> = ({ activeFilter, onFilterChange }) => {
    const { data: summary, isLoading } = useGetComplianceStatisticsQuery();

    const handleClick = (filterValue: ComplianceStatisticsFilter) => {
        onFilterChange(activeFilter === filterValue ? null : filterValue);
    };

    if (isLoading || !summary) {
        return <DashboardSkeleton />;
    }

    return (
        <div className={GRID_CLASS}>
            {CARDS.map(({ filterValue, labelId, countKey, tooltipText }) => (
                <div
                    key={filterValue}
                    onClick={() => handleClick(filterValue)}
                    className={cn(
                        'hover:shadow-card-raised cursor-pointer rounded-[7px] transition-[box-shadow,outline] duration-150',
                        activeFilter === filterValue && 'outline-primary outline-2'
                    )}
                >
                    <LabelCard
                        labelId={labelId}
                        content={summary[countKey]}
                        fontSize="large"
                        color={getColor(summary[countKey])}
                        tooltipText={tooltipText}
                    />
                </div>
            ))}
        </div>
    );
};

export default ComplianceDashboard;
